import { createApi } from '@reduxjs/toolkit/query/react'
import type {
  JoinLocationRequest,
  PrescriberLocationProfile,
  UpdateLocationProfileRequest,
} from '../../types/location'
import { axiosBaseQuery } from '../../services/baseQuery'
import { locationsApi } from './locationSlice'

export type JoinPrescriberLocationProfileRequest = { locationId: string } & JoinLocationRequest

export const prescriberLocationProfilesApi = createApi({
  reducerPath: 'prescriberLocationProfilesApi',
  tagTypes: ['PrescriberLocationProfiles'],
  baseQuery: axiosBaseQuery({ baseUrl: '/bff/prescribers' }),
  endpoints: (build) => ({
    getPrescriberLocationProfiles: build.query<PrescriberLocationProfile[], void>({
      query: () => ({
        url: '/location-profiles',
        method: 'get',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: 'PrescriberLocationProfiles' as const, id: p.id })),
              { type: 'PrescriberLocationProfiles', id: 'LIST' },
            ]
          : [{ type: 'PrescriberLocationProfiles', id: 'LIST' }],
    }),
    joinPrescriberLocationProfile: build.mutation<
      { profileLocationId: string },
      JoinPrescriberLocationProfileRequest
    >({
      query: (body) => ({
        url: '/location-profiles',
        method: 'post',
        data: body,
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [{ type: 'PrescriberLocationProfiles', id: 'LIST' }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(locationsApi.util.invalidateTags([{ type: 'Locations', id: 'LIST' }]))
        } catch {
          /* error handled by caller */
        }
      },
    }),
    updatePrescriberLocationProfile: build.mutation<
      { profileLocationId: string },
      { locationProfileId: string; data: UpdateLocationProfileRequest }
    >({
      query: ({ locationProfileId, data }) => ({
        url: `/location-profiles/${locationProfileId}`,
        method: 'patch',
        data,
      }),
      invalidatesTags: (_result, error, { locationProfileId }) =>
        error
          ? []
          : [
              { type: 'PrescriberLocationProfiles', id: locationProfileId },
              { type: 'PrescriberLocationProfiles', id: 'LIST' },
            ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(locationsApi.util.invalidateTags([{ type: 'Locations', id: 'LIST' }]))
        } catch {
          /* error handled by caller */
        }
      },
    }),
    leavePrescriberLocationProfile: build.mutation<
      { success: boolean },
      { locationProfileId: string }
    >({
      query: ({ locationProfileId }) => ({
        url: `/location-profiles/${locationProfileId}`,
        method: 'delete',
      }),
      invalidatesTags: (_result, error, { locationProfileId }) =>
        error
          ? []
          : [
              { type: 'PrescriberLocationProfiles', id: locationProfileId },
              { type: 'PrescriberLocationProfiles', id: 'LIST' },
            ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(locationsApi.util.invalidateTags([{ type: 'Locations', id: 'LIST' }]))
        } catch {
          /* error handled by caller */
        }
      },
    }),
  }),
})

export const {
  useGetPrescriberLocationProfilesQuery,
  useJoinPrescriberLocationProfileMutation,
  useUpdatePrescriberLocationProfileMutation,
  useLeavePrescriberLocationProfileMutation,
} = prescriberLocationProfilesApi
